import { apiLocations } from "@/api/location.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiLocationSavePayload } from "@/types/location.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";
import { escapeRegExp } from "lodash";

const locationsKey = createQueryKeys("locations", {
  countries: null,
  all: null,
  byListOfId: (ids: number[]) => ({
    queryKey: [ids],
    queryFn: async () => {
      const result = await apiLocations.getByListOfId(ids);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useCountries = (enabled = true) => {
  return useQuery({
    ...locationsKey.countries,
    queryFn: async () => {
      const result = await apiLocations.getAllCountries();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled: enabled,
  });
};

export const useLocations = (enabled = true) => {
  return useQuery({
    ...locationsKey.all,
    queryFn: async () => {
      const result = await apiLocations.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled: enabled,
  });
};

export const useLocationSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: ApiLocationSavePayload }) => {
      const result =
        id == 0 ? await apiLocations.create(payload) : await apiLocations.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: locationsKey.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useLocationDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiLocations.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: locationsKey.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useLocationsSearchInline = (enabled: boolean = true) => {
  const [searchKey, setSearchKey] = useState<string | undefined>(undefined);

  const { data, isLoading } = useLocations(enabled);

  const options = useMemo(() => {
    const allData = (data || []) as SelectItem[];
    const regex = new RegExp(escapeRegExp(searchKey), "i");
    return allData.filter((option) => regex.test(option.title));
  }, [data, searchKey]);

  function search(text: string | undefined) {
    setSearchKey(text);
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export const useLocationsByIds = ({
  ids,
  enabled = true,
}: {
  ids: number[];
  enabled?: boolean;
}) => {
  return useQuery({
    ...locationsKey.byListOfId(ids),
    enabled: enabled && ids.length > 0,
  });
};
